import { merge } from 'lodash-es';
import React from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { CSS } from '../../stitches.config';
import type { SpacingScale } from '../../utilities/shared/sizes';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { useSurfaceVariant } from '../../utilities/useSurfaceVariant';
import { getVariantShadowStyles } from '../../utilities/useVariantAwareShadow';

const AssetTilePositioner = styled('div', {
  display: 'flex',

  variants: {
    ratio: {
      'x-small': {
        widthAll: '100%',
        heightAll: '100%',
      },
      small: {
        widthAll: '64%',
        heightAll: '64%',
      },
      medium: {
        widthAll: '56%',
        heightAll: '56%',
      },
      large: {
        widthAll: '48%',
        heightAll: '48%',
      },
      'x-large': {
        widthAll: '48%',
        heightAll: '48%',
      },
    },
    unset: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      unset: true,
      css: {
        widthAll: '100%',
        heightAll: '100%',
      },
    },
  ],
});
AssetTilePositioner.displayName = 'AssetTilePositioner';

const AssetTileContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.bgApplicationLight,

  variants: {
    appearance: {
      visible: {},
      hidden: {},
    },
    radius: {
      'x-small': {
        borderRadius: '$4',
      },
      small: {
        borderRadius: '$6',
      },
      medium: {
        borderRadius: '$10',
      },
      large: {
        borderRadius: '$14',
      },
      'x-large': {
        borderRadius: '$20',
      },
    },
  },

  compoundVariants: [
    {
      appearance: 'hidden',
      css: {
        boxShadow: colors.transparent,

        [darkThemeSelector]: {
          boxShadow: colors.transparent,
        },
      },
    },
  ],
});
AssetTileContainer.displayName = 'AssetTileContainer';

export type AssetTileProps = {
  /**
   * The element to show inside of the tile.
   */
  children: React.ReactNode;
  /**
   * Additional CSS to apply to the tile container (e.g. variant-aware shadows).
   */
  css?: CSS;
  /**
   * Set a size of the component.
   */
  size?: SpacingScale;
  /**
   * Unsets the sizing of the SVG inside of the tile.
   */
  unset?: boolean;
};

const getTileSizeString = (size: number) => {
  switch (true) {
    case size >= 60:
      return 'x-large';
    case size >= 44:
      return 'large';
    case size >= 32:
      return 'medium';
    case size >= 20:
      return 'small';
    default:
      return 'x-small';
  }
};

export const AssetTile = React.forwardRef<HTMLDivElement, AssetTileProps>(
  ({ children, size = 28, unset = false, ...remaining }, forwardedRef) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const variant = useSurfaceVariant(containerRef);
    const shadowCSS = React.useMemo(() => getVariantShadowStyles(variant), [variant]);
    const isXSmall = getTileSizeString(size) === 'x-small';
    return (
      <AssetTileContainer
        ref={mergeRefs([containerRef, forwardedRef])}
        appearance={isXSmall ? 'hidden' : 'visible'}
        radius={getTileSizeString(size)}
        css={merge({
          ...(!isXSmall ? shadowCSS : {}),
          width: `$${size}`,
          maxWidth: `$${size}`,
          minWidth: `$${size}`,
          height: `$${size}`,
          maxHeight: `$${size}`,
          minHeight: `$${size}`,
        })}
        {...remaining}
      >
        <AssetTilePositioner ratio={getTileSizeString(size)} unset={unset}>
          {children}
        </AssetTilePositioner>
      </AssetTileContainer>
    );
  },
);
